import React, { useRef, useState } from 'react';
import { Button } from 'antd';
import { STRIPE_PUBLIC_KEY } from 'constants/stripe';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import EditPaymentContent from 'components/EditPaymentModal/Content';
import { DEPOSIT, CASHBACK_LIMIT } from '../../constants';
import s from './s.module.less';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

interface Props {
    panelCount: number;
    isGroup?: boolean;
    providerCount?: number;
    // 协议已签、押金账单已生成：不绑卡也算提交完成，由调用方按完成处理（不能回到已签的协议页）
    onAddLater: () => void;
    onSuccess: () => void;
    hidePaymentChangeMessageTips?: boolean;
}

const PaymentStepInner = ({
    panelCount,
    isGroup,
    providerCount,
    onAddLater,
    onSuccess,
    hidePaymentChangeMessageTips,
}: Props) => {
    const submitRef = useRef<(() => Promise<void>) | null>(null);
    const [loading, setLoading] = useState(false);

    const totalDeposit = panelCount * DEPOSIT;
    const cbCount = Math.min(panelCount, CASHBACK_LIMIT);
    const cbAmount = cbCount * DEPOSIT;

    const handleSubmit = async () => {
        if (submitRef.current) {
            setLoading(true);
            try {
                await submitRef.current();
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <>
            <div className={s.modalBody}>
                <div className={s.depositBanner}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                        <circle cx="10" cy="10" r="10" fill="#16A34A" />
                        <path
                            d="M6 10.5l2.5 2.5L14 7.5"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span>
                        We charge a ${DEPOSIT} deposit per panel
                        {isGroup && providerCount && providerCount > 1
                            ? ` per provider (${providerCount} providers)`
                            : ''}{' '}
                        so we can start your credentialing right away.
                        {panelCount <= CASHBACK_LIMIT ? (
                            <>
                                {' '}
                                Your deposit{panelCount > 1 ? 's come' : ' comes'} back to you as cashback after your
                                first insurance claim is submitted to Klarity.
                            </>
                        ) : (
                            <>
                                {' '}
                                For your first {CASHBACK_LIMIT} panels, the deposit comes back to you as cashback after
                                your first insurance claim is submitted to Klarity.
                            </>
                        )}
                        {panelCount > 0 && (
                            <>
                                {' '}
                                Total: <strong>${totalDeposit}</strong> ({panelCount} panel{panelCount > 1 ? 's' : ''}).
                                {cbCount > 0 && (
                                    <>
                                        {' '}
                                        First {cbCount} deposit{cbCount > 1 ? 's' : ''} (${cbAmount}) returned as
                                        cashback.
                                    </>
                                )}
                            </>
                        )}
                    </span>
                </div>

                <EditPaymentContent
                    hidePaymentChangeMessageTips={hidePaymentChangeMessageTips}
                    embedded
                    submitRef={submitRef}
                    onUpdatePaymentInfo={onSuccess}
                />
            </div>

            <div className={s.modalFooter}>
                <Button
                    type="primary"
                    block
                    size="large"
                    className={s.primaryBtn}
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Confirm and submit
                </Button>
                <Button block size="large" className={s.backBtn} onClick={onAddLater}>
                    Add payment later
                </Button>
            </div>
        </>
    );
};

const PaymentStep = (props: Props) => (
    <Elements stripe={stripePromise}>
        <PaymentStepInner {...props} />
    </Elements>
);

export default PaymentStep;
